// decision-answered-journal-event.ts — V2-0 (spec V2 §4.0, plan 20261006-1008
// phase-01).
//
// Event journal `decision_answered`: người đã chốt một câu hỏi. Đây là nguồn
// nhánh NGƯỜI của sổ quyết định (§4.1, "journal-backed") — trước V2-0 câu trả
// lời chỉ nằm trong interview.json, bị ghi đè khi người đổi ý, không có dấu
// vết ai chọn gì lúc nào.
//
// Idempotent theo `directive_id`: crash giữa lúc append journal và
// `markDirectiveProcessed` ⇒ lần xử lý lại thấy event đã có và KHÔNG ghi lần
// hai. Kiểm bằng quét journal (không thêm field vào projection mission.json —
// reducer bỏ qua kind này qua `default`, như mọi event audit khác).
import { join } from "node:path";
import { scanJournal } from "./journal-scan-report.js";
import type { MissionDelta } from "./mission-state-types.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import { needsDecisionQuestionTicket } from "./needs-decision-question-ticket-match.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

/** `directive_id` của mọi `decision_answered` đã ghi bền trong journal mission. */
export async function readJournaledDecisionDirectiveIds(controlRoot: string): Promise<Set<string>> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  const ids = new Set<string>();
  for (const record of scan.records) {
    const { kind, directive_id: directiveId } = record.delta;
    if (kind === "decision_answered" && typeof directiveId === "string") ids.add(directiveId);
  }
  return ids;
}

/**
 * `{question_id, ticket_id?, value, actor: "user:<id>", directive_id}`.
 * `ticket_id` (đọc từ nguyên id) vắng ở câu của cả mission.
 *
 * V2-0 ghi `value` — KHÔNG `option_id`: interview.json v1 chỉ lưu NHÃN option,
 * chưa có id thật. `option_id` dành cho V2-1 (E3 lưu cặp id→nhãn); journal là
 * nguồn bất biến của sổ quyết định nên một tên field không được đổi nghĩa giữa
 * hai bản (spec §4.0 `option_id|value`).
 */
export function decisionAnsweredDelta(
  question: InterviewQuestion,
  value: string | string[],
  actorId: string,
  directiveId: string,
): MissionDelta {
  const ticketId = needsDecisionQuestionTicket(question);
  return {
    kind: "decision_answered",
    question_id: question.id,
    ...(ticketId === undefined ? {} : { ticket_id: ticketId }),
    value,
    actor: `user:${actorId}`,
    directive_id: directiveId,
  };
}
